import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18N, I18NContext, GlobalContext, StyledInput } from 'commons';
import styled from 'styled-components';
import cs from 'classnames';
import { connect } from 'react-redux';
import * as signUpActions from 'state/actions/signUpActions';
import * as currentUserSelectors from 'state/selectors/currentUserSelectors';
import _ from 'lodash';
import { parsePhoneNumber } from '@eb/libphonenumber';
import ErrorMessage from 'components/forms/ErrorMessage';
import { convertHTMLTags } from 'utils';
import { Field } from 'formik';

const Container = styled.div`
  display: flex;
`;
const InputGroupText = styled.div`
  border-radius: 0 !important;
  border-inline-end: 0 !important;
`;

const PhoneNumberInputIntl = ({
  contactSelectedCountries,
  contactCountries,
  signUpSelectedCountries,
  signUpCountries,
  isEnableCountryCodeForMemberPortal,
  errors,
  hasTouched,
  setFieldError,
  setFieldTouched,
  pathLabel,
  setFieldValue,
  onChangeCallback,
  onCountryChangeCallback,
  setIsNeedValidation,
  isSignUp,
  getAsIsPhoneState,
  getAsIsState,
  setIscallOutMsgShow,
  itemProperty,
  autocomplete,
  ...props
}) => {
  /*
  Formik related:
    errors,
    hasTouched,
    setFieldError,
    setFieldTouched,
    setFieldValue
  Contact Path Only:
    contactSelectedCountries,
    contactCountries,
    Inherited from Parent:
      onChangeCallback,
      setIscallOutMsgShow,
      setIsNeedValidation
  Sign Up Only:
    getAsIsPhoneState,
    getAsIsState,
    signUpSelectedCountries,
    signUpCountries,
  Other: isSignUp
  */

  const i18n = useContext(I18NContext);
  const { organization: { defaultCountry: orgCountry = 'US' } } = useContext(GlobalContext);
  const UNSUPPORTED_GOOGLE_PHONE_NUMBER_REGION = ['PN', 'UM', 'AN', 'CS', 'AQ'];
  const { pathId, pathType, value, mandatory, editable, noValidate, countryCode, phoneExt, systemRequirement, code } = itemProperty;
  const { countryCodeName, inputName } = props;
  const { maxLength, minLength } = {
    maxLength: 20,
    minLength: 4
  };

  const convertPathLabel = convertHTMLTags(pathLabel);
  const countries = !isSignUp ? contactCountries : signUpCountries;
  const selectedCountries = !isSignUp ? contactSelectedCountries : signUpSelectedCountries;
  let countryList = isEnableCountryCodeForMemberPortal ? selectedCountries : countries;

  const lengthError = pathLabel ? `message.validation.invalid.number.length, ${i18n.get('profile.contact.path.number.label', convertPathLabel)}` : 'message.validation.phone_length_other';

  const [phoneNumber, setPhoneNumber] = useState(value);
  const [currentCountryCode, setCurrentCountryCode] = useState(countryCode);
  const [displayNumber, setDisplayNumber] = useState('');
  const [useAsIsDisabled, setUseAsIsDisabled] = useState(false);
  const [useAsIsChecked, setUseAsIsChecked] = useState(noValidate);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [hasUnselectedCountry, setHasUnselectedCountry] = useState(false);

  // TTY path only supports us and ca
  if (pathType === 'TTYTDD') {
    countryList = _.filter(countryList, country => ['US', 'CA'].includes(country.code));
  }

  useEffect(() => {
    initPhoneNumber();
  }, []);

  useEffect(() => {
    if (value !== phoneNumber) {
      initPhoneNumber();
      setPhoneNumber(value);
    }
  }, [value]);

  const initPhoneNumber = () => {
    let countryCodeTmp = currentCountryCode;
    if (value) {
      try {
        const defaultNumber = parsePhoneNumber(value, countryCode.toUpperCase());
        setDisplayNumber(defaultNumber.format('INTERNATIONAL'));
      } catch (error) {
        setDisplayNumber(value);
      } finally {
        setUseAsIsChecked(noValidate);
        countryCodeTmp = countryCode;
        // handle selected countries
        if (isEnableCountryCodeForMemberPortal) {
          setCurrentCountry(_.find(countries, item => item.code === countryCode));
          handleSelectedCountryList(countryCode);
        }
      }
    } else {
      // no value, set to default
      // If no item in countrylist, ignore
      if (countryList.length) {
        if (orgCountry && _.find(countryList, item => item.code === orgCountry)) {
          countryCodeTmp = orgCountry;
        } else {
          countryCodeTmp = countryList[0].code;
        }
      } else {
        countryCodeTmp = '';
      }
    }

    setCurrentCountryCode(countryCodeTmp);
    handleSelectedCountryList(countryCodeTmp);

    // handle use as is
    if (UNSUPPORTED_GOOGLE_PHONE_NUMBER_REGION.includes(countryCodeTmp)) {
      setUseAsIsDisabled(true);
      if (!noValidate) {
        onUseAsIsChange(true);
      }
    }
  };

  const handleSelectedCountryList = iso2 => {
    if (!iso2) {
      setFieldError(`${inputName}countryCode`, 'ssp.country.code.invalid');
      setHasUnselectedCountry(true);
      return false;
    } if (editable && isEnableCountryCodeForMemberPortal) {
      if (!_.find(selectedCountries, item => item.code === iso2)) {
        setFieldError(`${inputName}countryCode`, 'ssp.country.code.invalid');
        setHasUnselectedCountry(true);
        return false;
      }
      delete errors[`${inputName}countryCode`];
      return true;
    }
  };

  const validatePhoneNumber = (number, iso2) => {
    setFieldTouched(inputName, true);
    if (!number) {
      if (mandatory) {
        setFieldError(inputName, 'ssp.checked.phoneNum');
        return false;
      }
      delete errors[inputName];
      return true;
    } if (number) {
      setFieldTouched(inputName, true);
      if (useAsIsChecked && (phoneNumber.length > maxLength || phoneNumber.length < minLength)) {
        setFieldError(inputName, lengthError);
        return false;
      }
      let result;
      try {
        const phoneNumberParsed = parsePhoneNumber(number, iso2);
        const numberParsed = parsePhoneNumber(phoneNumber, iso2);
        result = phoneNumberParsed.isValid() && numberParsed.isValid();
      } catch (e) {
        // nothing
      } finally {
        if (result) {
          delete errors[inputName];
          return true;
        }
        setFieldError(inputName, 'ssp.checked.phoneNum');
        return false;
      }
    }
  };

  const onUseAsIsChange = (checked, iso2, number) => {
    setUseAsIsChecked(checked);

    if (checked) {
      if (phoneNumber && (phoneNumber.length > maxLength || phoneNumber.length < minLength)) {
        setFieldError(inputName, lengthError);
      } else {
        delete errors[inputName];
      }
    } else {
      validatePhoneNumber(number || displayNumber, currentCountryCode);
    }

    if (iso2) {
      onChangeCallback({
        countryCode: iso2,
        noValidate: checked
      }, { fromUseAsIs: true });
    } else {
      onChangeCallback({
        noValidate: checked
      }, { fromUseAsIs: true });
    }

    if (inputName.includes('smsPhone')) {
      getAsIsState({
        smsValidate: checked,
      });
    }
    if (inputName.includes('mobilePhone')) {
      getAsIsPhoneState(
        {
          phoneValidate: checked,
        }
      );
    }
  };

  const onInputChange = (e) => {
    const { value } = e.target;
    const reg = new RegExp(/^[^a-zA-Z]{0,}$/);
    let number;
    let shouldUseAsIsChecked = useAsIsChecked;
    if (reg.test(value)) {
      if (!hasTouched) {
        setFieldTouched(inputName, true);
      }
      if (!UNSUPPORTED_GOOGLE_PHONE_NUMBER_REGION.includes(currentCountryCode)) {
        shouldUseAsIsChecked = false;
        setUseAsIsChecked(shouldUseAsIsChecked);
      }
      let savePhoneNumber = value;
      try {
        const phoneNumberParsed = parsePhoneNumber(value, currentCountryCode.toUpperCase());
        savePhoneNumber = phoneNumberParsed.nationalNumber;
        setDisplayNumber(phoneNumberParsed.format('INTERNATIONAL'));
      } catch (error) {
        // set the number as filled, get number without country code
        savePhoneNumber = value.slice(value.indexOf(' ') + 1);
        setDisplayNumber(value);
      } finally {
        setPhoneNumber(savePhoneNumber);
        errors[inputName] && delete errors[inputName];
        setFieldValue(inputName, savePhoneNumber);
        onChangeCallback && onChangeCallback({
          value: savePhoneNumber,
          noValidate: shouldUseAsIsChecked
        });
        if (inputName.includes('smsPhone')) {
          getAsIsState({
            smsValidate: shouldUseAsIsChecked,
          });
        }
        if (inputName.includes('mobilePhone')) {
          getAsIsPhoneState(
            {
              phoneValidate: shouldUseAsIsChecked,
            }
          );
        }
        if (shouldUseAsIsChecked) {
          if (!savePhoneNumber && (phoneExt || systemRequirement || systemRequirement !== 'select')) {
            setFieldError(inputName, lengthError);
          }
        }
        if (!mandatory && !savePhoneNumber && !phoneExt && systemRequirement === 'select') {
          delete errors[inputName];
          delete errors.pagerService;
        }
        setIsNeedValidation(false);
      }
    }
  };

  const onInputBlur = (e) => {
    const { value } = e.target;
    if (!isSignUp) {
      const { nativeEvent } = e;
      if (nativeEvent && nativeEvent.formSubmit) {
        const { formSubmit } = nativeEvent;
        formSubmit[inputName] = true;
        const { validationCtrl: { size, resolver } } = nativeEvent;
        const { length } = Object.keys(formSubmit);
        if (length === size) {
          resolver();
        }
      }
    }
    if (!currentCountryCode) return;

    if (isEnableCountryCodeForMemberPortal && !handleSelectedCountryList(currentCountryCode)) {
      return;
    }

    if (useAsIsChecked) {
      if (phoneNumber && (phoneNumber.length > maxLength || phoneNumber.length < minLength)) {
        setFieldError(inputName, lengthError);
      }
      return;
    }
    validatePhoneNumber(value, currentCountryCode);
  };

  const onCountryChange = (e) => {
    const { value } = e.target;
    const iso2 = value.toUpperCase();
    setCurrentCountryCode(iso2);
    handleSelectedCountryList(iso2);

    // for updating on sign up
    setFieldValue(`${pathId}CountryCode`, iso2, false);
    // for updating on contact path
    onChangeCallback({
      countryCode: iso2
    });

    if ((!code || code !== 'ONEWAYSMS') && pathType === 'SMS') {
      if (iso2 === 'US') {
        setIscallOutMsgShow(true);
      } else {
        onCountryChangeCallback();
      }
    }

    // validate phone number again when country is changed
    if (value) {
      if (!UNSUPPORTED_GOOGLE_PHONE_NUMBER_REGION.includes(iso2)) {
        setUseAsIsDisabled(false);
        try {
          const phoneNumberParsed = parsePhoneNumber(phoneNumber, iso2);
          const number = phoneNumberParsed.format('INTERNATIONAL');
          setDisplayNumber(number);
          onUseAsIsChange(false, iso2, number);
        } catch (error) {
          // nothing for now
        }
      } else {
        // if google unsupported number, no validation, no format, use as-is checked
        setDisplayNumber(phoneNumber);
        setUseAsIsDisabled(true);
        onUseAsIsChange(true, iso2, phoneNumber);
      }
    }
  };
  return (
    <Container className="PhoneNumberInputIntl row">
      <div className="col-md-4">
        <label className="font-weight-normal" htmlFor={`${inputName}countryCode`}>
          <span className="sr-only">{`${convertPathLabel} `}</span>
          <I18N>ssp.countryAndRegion.label</I18N>
        </label>
        <div className="input-group mb-2">
          <Field
            name={`${inputName}countryCode`}
            id={`${inputName}countryCode`}
            disabled={!editable}
            onChange={onCountryChange}
            className={cs('form-control', { valid_error: errors[`${inputName}countryCode`] })}
            value={currentCountryCode && currentCountryCode.toLowerCase()}
            validate={() => errors[`${inputName}countryCode`] || ''}
            component="select"
            {...(errors[`${inputName}countryCode`] ? { 'aria-describedby': `${inputName}CountryError` } : {})}
          >

            {
              hasUnselectedCountry && currentCountry &&
              <option value={currentCountry.code.toLowerCase()} key={currentCountry.name}>
                {currentCountry.name}
              </option>
            }
            {_.map(countryList, (item) => (
              <option value={item.iso2} key={item.name}>
                {item.name}
              </option>
            ))}
          </Field>
        </div>
        {
          errors[`${inputName}countryCode`] && (
            <ErrorMessage id={`${inputName}CountryError`} aria-live="assertive">
              <I18N>{errors[`${inputName}countryCode`]}</I18N>
            </ErrorMessage>
          )
        }
      </div>
      <div className="col-md-8">
        <label className="font-weight-normal" htmlFor={inputName}>
          <I18N>{`profile.contact.path.number.label, ${convertPathLabel}`}</I18N>
        </label>
        <div className="input-group mb-2">
          <InputGroupText className="input-group-text">
            <div className={currentCountryCode ? `iti-flag ${currentCountryCode.toLowerCase()}` : 'iti-flag'} />
          </InputGroupText>
          <Field
            autoComplete={autocomplete}
            className={cs('form-control', { valid_error: hasTouched && errors[inputName], js_blur: editable })}
            disabled={!editable}
            type="tel"
            id={inputName}
            name={inputName}
            value={displayNumber}
            onChange={onInputChange}
            onBlur={onInputBlur}
            validate={() => errors[inputName] || ''}
            {...((hasTouched && errors[inputName]) ? { 'aria-describedby': `${pathId}Desc` } : {})}
          />
        </div>
        {
          hasTouched && errors[inputName] && (
            <ErrorMessage id={`${pathId}Desc`} aria-live="assertive">{errors[inputName]}</ErrorMessage>
          )}
        {
          ((displayNumber && errors[inputName] && hasTouched) || useAsIsChecked) &&
          <div>
            <label className="normal" htmlFor={`${pathId}-useAsIs`} id={`${pathId}BypassLabel`}>
              <input
                type="checkbox"
                name={`${pathId}-useAsIs`}
                id={`${pathId}-useAsIs`}
                onChange={e => { onUseAsIsChange(e.target.checked); }}
                checked={useAsIsChecked}
                disabled={useAsIsDisabled}
              />
              <I18N>ssp.myprofile.bypass.phone.validation</I18N>
            </label>
          </div>
        }
      </div>
    </Container>
  );
};

PhoneNumberInputIntl.propTypes = {
  contactSelectedCountries: PropTypes.arrayOf(PropTypes.object),
  contactCountries: PropTypes.arrayOf(PropTypes.object),
  signUpSelectedCountries: PropTypes.arrayOf(PropTypes.object),
  signUpCountries: PropTypes.arrayOf(PropTypes.object),
  isEnableCountryCodeForMemberPortal: PropTypes.bool.isRequired,
  errors: PropTypes.shape().isRequired,
  hasTouched: PropTypes.bool,
  setFieldError: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onChangeCallback: PropTypes.func,
  onCountryChangeCallback: PropTypes.func,
  setIsNeedValidation: PropTypes.func,
  pathLabel: PropTypes.string.isRequired,
  isSignUp: PropTypes.bool,
  getAsIsState: PropTypes.func,
  getAsIsPhoneState: PropTypes.func,
  setIscallOutMsgShow: PropTypes.func.isRequired,
  itemProperty: PropTypes.shape().isRequired,
  autocomplete: PropTypes.string,
};

PhoneNumberInputIntl.defaultProps = {
  contactSelectedCountries: [{}],
  contactCountries: [{}],
  signUpSelectedCountries: [{}],
  signUpCountries: [{}],
  hasTouched: false,
  onChangeCallback: () => { },
  onCountryChangeCallback: () => { },
  setIsNeedValidation: () => { },
  isSignUp: false,
  getAsIsState: () => { },
  getAsIsPhoneState: () => { },
  autocomplete: "off",
};

const mapStateToProps = (state) => ({
  contactCountries: currentUserSelectors.selectContactPathCountries(state),
  contactSelectedCountries: currentUserSelectors.selectContactPathSelectedCountries(state),
});
const mapDispatchToProps = (dispatch) => ({
  getAsIsState: (data) => dispatch(signUpActions.getAsIsState.action(data)),
  getAsIsPhoneState: (data) => dispatch(signUpActions.getAsIsPhone.action(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PhoneNumberInputIntl);
